// Pinia store：用户认证状态管理

import { defineStore } from 'pinia'
import {
  login as apiLogin,
  loginStart as apiLoginStart,
  loginVerify as apiLoginVerify,
  logout as apiLogout,
  register as apiRegister,
  updateTenantSlug as apiUpdateTenantSlug,
  getMe,
  changePassword,
  establishPortalSession as apiEstablishPortalSession,
  restorePortalSession as apiRestorePortalSession,
  logoutPortalSession as apiLogoutPortalSession,
} from '@/api/auth'
import { setTenantId, clearTenantId } from '@/api/request'
import { useEntitlementsStore } from '@/stores/entitlements'
import { useLocaleStore } from '@/stores/locale'
import type { UserInfo } from '@/types'

interface AuthState {
  token: string
  refreshToken: string
  tenantId: string
  user: UserInfo | null
  remember: boolean
}

// auth 存储读取：记住登录的存 localStorage（持久7天），不记住的存 sessionStorage（关浏览器即失效）
function readAuth(key: string): string {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? ''
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: readAuth('access_token'),
    refreshToken: readAuth('refresh_token'),
    tenantId: readAuth('tenant_id'),
    user: null,
    // 有 localStorage token 说明上次勾选了「记住登录」
    remember: localStorage.getItem('access_token') !== null,
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    hasPermission: (state) => (code: string) => (
      !!state.user?.role_is_system || !!state.user?.permissions?.includes(code)
    ),
  },

  actions: {
    // 登录第一步：受信任设备可直接发现租户，否则先验证邮箱。
    async startLogin(email: string, turnstileToken = '', forceEmailVerification = false) {
      const trustedDeviceToken = localStorage.getItem('trusted_device_token') || ''
      return await apiLoginStart(email, trustedDeviceToken, turnstileToken, forceEmailVerification) as any
    },

    async verifyLogin(email: string, challengeId: string, code: string) {
      return await apiLoginVerify(email, challengeId, code) as any
    },

    // 最后消费短期票据签发 JWT；remember 决定 localStorage 或 sessionStorage。
    async login(
      tenantId: string,
      loginTicket: string,
      password: string,
      remember: boolean = false,
    ) {
      const data: any = await apiLogin(tenantId, loginTicket, password, remember)
      if (!data?.access_token) return data
      this.remember = remember
      const storage = remember ? localStorage : sessionStorage
      // 切换存储前清旧 storage，避免「不记住」时 localStorage 残留导致仍持久
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('tenant_id')
      this.token = data.access_token
      this.refreshToken = data.refresh_token
      this.tenantId = tenantId
      setTenantId(tenantId)
      storage.setItem('access_token', this.token)
      storage.setItem('refresh_token', this.refreshToken)
      storage.setItem('tenant_id', this.tenantId)
      if (remember && data.trusted_device_token) {
        localStorage.setItem('trusted_device_token', data.trusted_device_token)
      } else {
        localStorage.removeItem('trusted_device_token')
      }
      // 拉取完整用户信息（含头像），覆盖 login 响应中可能缺失的字段
      await this.fetchUser()
      await apiEstablishPortalSession(this.refreshToken, remember)
      localStorage.setItem('last_login_method', data.login_method === 'email_code' ? 'email_code' : 'password')
      return data
    },

    // Google OAuth 登录回调：后端已签发 token，前端直接存 + 拉 user info
    async loginWithToken(
      access: string,
      refresh: string,
      tenantId: string,
      remember: boolean = true,
      method: string = 'google',
    ) {
      this.remember = remember
      const storage = remember ? localStorage : sessionStorage
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('tenant_id')
      this.token = access
      this.refreshToken = refresh
      this.tenantId = tenantId
      setTenantId(tenantId)
      storage.setItem('access_token', access)
      storage.setItem('refresh_token', refresh)
      storage.setItem('tenant_id', tenantId)
      await this.fetchUser()
      await apiEstablishPortalSession(this.refreshToken, remember)
      localStorage.setItem('last_login_method', method)
    },

    // 合并注册：建租户 + owner 账号
    async register(
      companyName: string,
      companySlug: string,
      nickname: string,
      email: string,
      password: string,
      registrationTicket: string,
    ) {
      const data: any = await apiRegister(
        companyName,
        companySlug,
        nickname,
        email,
        password,
        registrationTicket,
      )
      this.remember = true  // 注册后默认记住登录
      this.token = data.access_token
      this.refreshToken = data.refresh_token
      const tid = data.user?.tenant_id || ''
      this.tenantId = tid
      if (tid) setTenantId(tid)
      localStorage.setItem('access_token', this.token)
      localStorage.setItem('refresh_token', this.refreshToken)
      if (tid) localStorage.setItem('tenant_id', tid)
      // 拉取完整用户信息（含头像）
      await this.fetchUser()
      await apiEstablishPortalSession(this.refreshToken, true)
      return data
    },

    async restorePortalSession() {
      const data: any = await apiRestorePortalSession()
      if (!data?.logged_in || !data.access_token || !data.refresh_token || !data.tenant_id) return false
      this.remember = false
      this.token = data.access_token
      this.refreshToken = data.refresh_token
      this.tenantId = data.tenant_id
      this.user = data.user || null
      setTenantId(this.tenantId)
      sessionStorage.setItem('access_token', this.token)
      sessionStorage.setItem('refresh_token', this.refreshToken)
      sessionStorage.setItem('tenant_id', this.tenantId)
      if (this.user) {
        sessionStorage.setItem('user_info', JSON.stringify(this.user))
        useLocaleStore().syncFromProfile(this.user.language, this.user.timezone)
      }
      return true
    },

    async syncPortalSession() {
      const refreshToken = this.refreshToken || readAuth('refresh_token')
      if (!refreshToken) return false
      await apiEstablishPortalSession(refreshToken, this.remember)
      return true
    },

    // 更新当前租户 slug
    async updateTenantSlug(slug: string) {
      await apiUpdateTenantSlug(slug)
      if (this.user) this.user.tenant_slug = slug
    },

    // 获取用户信息
    async fetchUser() {
      const data: any = await getMe()
      this.user = data
      useLocaleStore().syncFromProfile(data.language, data.timezone)
      const storage = this.remember ? localStorage : sessionStorage
      storage.setItem('user_info', JSON.stringify(data))
      return data
    },

    // 修改密码
    async changePassword(newPassword: string) {
      await changePassword(newPassword)
      await this.logout()
    },

    // 退出登录
    async logout() {
      const refreshToken = this.refreshToken || readAuth('refresh_token')
      this.token = ''
      this.refreshToken = ''
      this.tenantId = ''
      this.user = null
      this.remember = false
      useEntitlementsStore().reset()
      useLocaleStore().syncFromProfile()
      clearTenantId()
      // 两种 storage 都清，确保彻底登出
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('tenant_id')
      localStorage.removeItem('user_info')
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('refresh_token')
      sessionStorage.removeItem('tenant_id')
      sessionStorage.removeItem('user_info')
      const requests: Promise<unknown>[] = [apiLogoutPortalSession()]
      if (refreshToken) requests.push(apiLogout(refreshToken))
      await Promise.allSettled(requests)
    },
  },
})
